"use client";

/**
 * Superscript numbered citation marker that attaches a claim to its source.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`.rv-cite`, the
 * `Cite` component).
 *
 * Deliberately separate from `data-display/CitationChip`: that chip is an
 * inline mono `[n]` badge with `active` / `empty` states, 20×18 and
 * `rounded-[3px]`; `.rv-cite` is a 15×15 `vertical-align: super` marker in the
 * body font at 9.5px/700 on `--stone-50` with `--radius-xs`. Threading a
 * superscript variant through `CitationChip` would fork a shared
 * `data-display/` primitive with existing callers into two designs for no
 * shared behaviour.
 */

export interface ReportCitationMarkerProps {
  n: number;
  onClick?: (n: number) => void;
}

const MARKER_CLASS = [
  "inline-flex items-center justify-center align-super",
  "min-w-[15px] h-[15px] px-[3px] ml-1",
  "font-[family-name:var(--font-body)] text-[9.5px] font-bold leading-none",
  "text-(--fg-secondary) bg-(--bg-hover)",
  "border border-(--border-default) rounded-(--radius-xs)",
  "transition-colors duration-300 ease-in-out",
].join(" ");

/**
 * Renders nothing for a falsy citation number, matching the design's `Cite`.
 * Without `onClick` it renders as static text rather than a dead button.
 */
export function ReportCitationMarker({
  n,
  onClick,
}: ReportCitationMarkerProps) {
  if (!n) return null;

  if (!onClick) {
    return <span className={MARKER_CLASS}>{n}</span>;
  }

  return (
    <button
      type="button"
      className={[
        MARKER_CLASS,
        "cursor-pointer",
        "hover:text-(--fg-brand) hover:border-(--border-strong)",
      ].join(" ")}
      title="View source"
      aria-label={`View source ${n}`}
      onClick={() => onClick(n)}
    >
      {n}
    </button>
  );
}
